import * as Sentry from '@sentry/browser'
import { createContext, useContext, useCallback, useReducer } from 'react'
import { useIntl } from 'react-intl'
import { matchPath, useMatch, useLocation } from 'react-router-dom'
import { useNotification } from '@nfinite/react-hubstairs/components/Notification'
import useNavigate from 'hooks/useNavigate'
import { safeAssignUrl } from 'utils/url'

import { RESET_PASSWORD_UPDATE, RESET_PASSWORD_FIRST_CONNECTION, HOME, CONNECTION } from 'routes'
import { USER_STATUS } from 'utils/users'
import { getActiveMainContracts } from 'utils/contracts'
import { HUBSTAIRS_ID } from 'utils/organizations'

import { useApplication, ApplicationContext } from 'context/Application'
import { useQuery } from 'requests'
import { hasErrorCode, ERROR_CODE } from 'requests/errors'
import { GET_PROFILE } from './query'

import { LoadingDots } from '@nfinite/react-hubstairs/components'
import translations from './translations'
import { parse } from 'qs'
import { useContractType } from 'context/Organization'

const LOGIN_SESSION_STORAGE = 'hub-login-redirect'

export const ProfileContext = createContext()
const ProfileDispatchContext = createContext()

const SET_PROFILE = 'set user data'
const SET_PROFILE_CHECK = 'set initial user data and checkCompleted: true'

export function getActiveOrganizations(profile) {
  return profile?.organizations && profile?.organizations.filter(({ status }) => status === USER_STATUS.ENABLED)
}

function hasHubstairsOrganization(activeOrganizations) {
  return activeOrganizations.some(({ organization }) => organization.id === HUBSTAIRS_ID)
}

function useUpdateApplication() {
  const { pushError } = useNotification()
  const { formatMessage } = useIntl()
  const { replace } = useNavigate()

  const { setLanguage, setOrganization } = useApplication()
  const { organization: organizationContext } = useContext(ApplicationContext)

  const updateApplication = useCallback(
    profile => {
      const language = profile?.userPreferences?.language
      setLanguage(language)

      const activeOrganizations = getActiveOrganizations(profile)

      switch (true) {
        // case logout, not authenticated
        case !profile:
          setOrganization(undefined)
          break
        // case user mono-organization
        case activeOrganizations?.length === 1:
          setOrganization(activeOrganizations[0].organization.id)
          break
        // case user multi-organization
        case activeOrganizations?.length > 1: {
          const exists = activeOrganizations.find(({ organization }) => organization.id === organizationContext)
          if (!!organizationContext && !exists) {
            if (hasHubstairsOrganization(activeOrganizations)) {
              setOrganization(HUBSTAIRS_ID)
            } else {
              replace(HOME())
              pushError(formatMessage(translations.accessDenied))
              setOrganization(undefined)
            }
          }
          break
        }
        default:
          break
      }
    },
    [setLanguage, setOrganization, organizationContext, pushError, formatMessage, replace],
  )

  return updateApplication
}

function init() {
  return { profile: undefined, checkCompleted: false }
}

function reducer(state, action) {
  const { type, payload } = action

  switch (type) {
    case SET_PROFILE:
      return { ...state, profile: payload }
    case SET_PROFILE_CHECK:
      return { ...state, profile: payload, checkCompleted: true }
    default:
      throw new Error()
  }
}

/**
 * The ProfileProvider wont render the rest of the app before doing a check on the user authentication status
 * The ProfileProvider will hold the current user data
 *
 * https://kentcdodds.com/blog/authentication-in-react-applications
 *
 */
function ProfileProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, init())
  const isFirstConnection = useMatch({ path: RESET_PASSWORD_FIRST_CONNECTION(), end: false })
  const isResetPassword = useMatch({ path: RESET_PASSWORD_UPDATE(), end: false })

  return (
    <ProfileContext.Provider value={state}>
      <ProfileDispatchContext.Provider value={dispatch}>
        {isFirstConnection || isResetPassword || state.checkCompleted ? (
          children
        ) : (
          <ProfileBlocker>{children}</ProfileBlocker>
        )}
      </ProfileDispatchContext.Provider>
    </ProfileContext.Provider>
  )
}

/**
 * Ensure that application doesnt render before /me is completed
 */
function ProfileBlocker() {
  const { setProfileCheck } = useProfileDispatch()

  useQuery(GET_PROFILE, {
    variables: {
      contractFilter: getActiveMainContracts(),
    },
    onCompleted: data => setProfileCheck(data.me),
    onError: (error, { notifyError }) => {
      if (hasErrorCode(error, ERROR_CODE.ORGANIZATION_TRIAL_ENDED)) {
        notifyError({ message: translations.trialEnded })
      } else if (hasErrorCode(error, ERROR_CODE.USER_DISABLED)) {
        notifyError({ message: translations.disabledUser })
      }
      setProfileCheck(undefined)
    },
    noThrow: [ERROR_CODE.USER_DISABLED, ERROR_CODE.ORGANIZATION_TRIAL_ENDED, ERROR_CODE.NOT_LOGGED_IN],
  })

  return <LoadingDots position="absolute" />
}

function useProfileState() {
  const { profile } = useContext(ProfileContext) || {}
  return { profile, isAuthenticated: !!profile }
}

function setSentryContext(profile) {
  Sentry.configureScope(() => {
    Sentry.setUser({
      id: profile?.id,
      role: profile?.role?.value,
    })
  })
}

function useProfileDispatch() {
  const { pathname, search } = useLocation()
  const isConnection = matchPath({ path: CONNECTION(), end: false }, pathname)

  const updateApplication = useUpdateApplication()
  const dispatch = useContext(ProfileDispatchContext)

  const setProfile = useCallback(
    profile => {
      dispatch({ type: SET_PROFILE, payload: profile })
      updateApplication(profile)
      setSentryContext(profile)
    },
    [dispatch, updateApplication],
  )

  const setProfileCheck = useCallback(
    payload => {
      const parsedQs = parse(search, { ignoreQueryPrefix: true }) ?? {}
      if (payload && parsedQs.redirectUrl) {
        // Authenticated and has redirect
        return safeAssignUrl({ url: parsedQs.redirectUrl, throwInvalid: true })
      }

      dispatch({ type: SET_PROFILE_CHECK, payload })

      if (payload) {
        // Authenticated
        updateApplication(payload)
        setSentryContext(payload)
      } else {
        // Not authenticated
        if (!isConnection) {
          sessionStorage.setItem(LOGIN_SESSION_STORAGE, JSON.stringify({ pathname, ...parsedQs }))
        }
      }
    },
    [dispatch, updateApplication, isConnection, pathname, search],
  )

  return { setProfile, setProfileCheck }
}

function useProfile() {
  const { profile, isAuthenticated } = useProfileState()
  const { setProfile } = useProfileDispatch()

  const isGlobalRole = profile && !!profile.role

  const activeOrganizations = getActiveOrganizations(profile)

  const contractType = useContractType({ activeOrganizations })

  return {
    profile,
    isAuthenticated,
    isGlobalRole,
    activeOrganizations,
    setProfile,
    contractType,
  }
}

export { ProfileProvider, useProfile }
